import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import { useAuth } from '../context/AuthContext';

const STATUSES = ['abierto', 'en_curso', 'esperando_cliente', 'resuelto', 'cerrado'];
const STATUS_LABELS = {
  abierto: 'Abierto',
  en_curso: 'En curso',
  esperando_cliente: 'Esperando cliente',
  resuelto: 'Resuelto',
  cerrado: 'Cerrado'
};

const PRIORITY_COLORS = {
  baja: '#3b82f6',
  media: '#f59e0b',
  alta: '#ef4444',
  urgente: '#7f1d1d'
};

const TicketsKanban = () => {
  const { hasPermission } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);
  const modal = useModal();

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const res = await api.get('/tickets');
      setTickets(res.data);
    } catch (error) {
      modal.error('Error cargando tickets');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => {
    return STATUSES.reduce((acc, status) => {
      acc[status] = tickets.filter((ticket) => ticket.status === status);
      return acc;
    }, {});
  }, [tickets]);

  const moveTicket = async (ticketId, nextStatus) => {
    const ticket = tickets.find((item) => item.id === ticketId);
    if (!ticket || ticket.status === nextStatus) return;

    try {
      const res = await api.put(`/tickets/${ticketId}`, { status: nextStatus });
      setTickets((prev) => prev.map((item) => (item.id === ticketId ? res.data : item)));
    } catch (error) {
      modal.error(error.response?.data?.message || 'No se pudo mover el ticket');
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Kanban de tickets</h1>
          <p className="page-subtitle">Arrastra las tarjetas entre columnas para cambiar estado</p>
        </div>
      </div>

      <div className="kanban-board">
        {STATUSES.map((status) => (
          <div
            key={status}
            className="kanban-column"
            onDragOver={(event) => event.preventDefault()}
            onDrop={async () => {
              if (!hasPermission('tickets', 'edit')) return;
              if (draggedId !== null) {
                await moveTicket(draggedId, status);
              }
              setDraggedId(null);
            }}
          >
            <div className="kanban-column-header">
              <h3>{STATUS_LABELS[status]}</h3>
              <span>{columns[status].length}</span>
            </div>

            <div className="kanban-column-body">
              {columns[status].map((ticket) => (
                <div
                  key={ticket.id}
                  className="kanban-card"
                  draggable={hasPermission('tickets', 'edit')}
                  onDragStart={() => setDraggedId(ticket.id)}
                >
                  <div className="kanban-card-head">
                    <strong>{ticket.code}</strong>
                    <span
                      className="kanban-priority-dot"
                      style={{ background: PRIORITY_COLORS[ticket.priority] || '#64748b' }}
                    />
                  </div>
                  <p>{ticket.subject}</p>
                  <small>{ticket.customer?.name}</small>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TicketsKanban;
